import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Activity, Wifi, Users, Clock, AlertTriangle } from 'lucide-react';
import { useStreamHealth } from '@/hooks/useStreamHealth';
import { toast } from 'sonner';

interface StreamHealthMonitorProps {
  streamId?: string;
  isLive?: boolean;
  viewerCount?: number;
}

export const StreamHealthMonitor = ({ streamId, isLive, viewerCount = 0 }: StreamHealthMonitorProps) => {
  const health = useStreamHealth({
    streamId,
    isLive,
    onHealthChange: (newHealth) => {
      // Alert on poor connection
      if (newHealth.status === 'poor' && newHealth.connectionQuality < 70) {
        toast.error('⚠️ Poor connection quality detected!', {
          description: 'Your stream quality may be affected. Check your internet connection.',
          duration: 5000,
        });
      }
    },
    warningThreshold: 85,
    criticalThreshold: 70,
  });

  const statusColors = {
    excellent: 'bg-green-500',
    good: 'bg-blue-500',
    fair: 'bg-yellow-500',
    poor: 'bg-red-500',
  };

  const statusIcons = {
    excellent: null,
    good: null,
    fair: <AlertTriangle className="h-4 w-4" />,
    poor: <AlertTriangle className="h-4 w-4" />,
  };

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isLive) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Stream health monitoring will appear when live</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Stream Health
          </span>
          <Badge className={statusColors[health.status]} variant="secondary">
            <span className="flex items-center gap-1">
              {statusIcons[health.status]}
              {health.status.toUpperCase()}
            </span>
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-muted-foreground">
              <Wifi className="h-4 w-4" />
              Connection Quality
            </span>
            <span className="font-semibold">{health.connectionQuality.toFixed(0)}%</span>
          </div>
          <Progress value={health.connectionQuality} className="h-2" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Bitrate</p>
            <p className="text-lg font-semibold">{health.bitrate.toFixed(0)} kbps</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Frame Rate</p>
            <p className="text-lg font-semibold">{health.framerate.toFixed(1)} fps</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Dropped Frames</p>
            <p className="text-lg font-semibold">{health.droppedFrames.toFixed(2)}%</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Uptime
            </p>
            <p className="text-lg font-semibold font-mono">{formatUptime(health.uptime)}</p>
          </div>
        </div>

        <div className="pt-4 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />
              Live Viewers
            </span>
            <span className="text-2xl font-bold">{viewerCount}</span>
          </div>
        </div>

        {/* Health warning */}
        {!health.isHealthy && (
          <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Stream quality is degraded
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Check your internet connection and consider lowering quality settings
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
